import moment from 'moment';
import classNames from 'classnames';
import React, { useCallback, useState, useRef, useMemo } from 'react';
import {
  Col,
  Form,
  Row,
  Button,
  Nav,
  Tab,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Datepicker } from '../../../../../components/form/Datepicker';
import { FormGroup } from '../../../../../components/form/FormGroup';
import { Input } from '../../../../../components/form/Input';
import InputLabel from '../../../../../components/form/InputLabel';
import { SelectDropdownControlled } from '../../../../../components/form/Selects';
import { CreatableDropdownOption } from '../../../../../components/Select/types';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  ExtraFieldInput,
  useCreateExpenseMutation,
  useExpenseFormDataQuery,
} from '../../../__generated__/graphql';
import { mapExpenseCategory, mapMerchant, mapProject } from './mappings';
import {
  ExtraFieldsArea,
  ExtraFieldsForm,
  ExtraFieldsSupportedObject,
} from '../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../components/ExtraFields/types';
import { ExpenseItemForm, ExpenseItemFormRef } from './ExpenseItemForm';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FlairButton from '../../../../../components/button';
import { collectFiles } from '../../../../../utils/file';
import { fileSizeFormatter } from '../../../../../utils/file';

type Props = {
  onSave: () => void;
};

type FormData = {
  fromDate: Date;
  toDate?: Date | null;
  fromTime: string;
  toTime: string;
  amount: number;
  category: string;
  project: string | null;
  description: string;
};

type ExpenseItemState = {
  localId: string;
  recordTypeId: string;
  manualAmount?: number | null;
  merchantName: string;
  merchantId: string;
  extraFields?: ExtraFieldInput[];
  files?: File[];
};

type File = {
  fileName: string;
  fileBase64: string;
  size: number;
};

const formSchema: yup.ObjectSchema<FormData> = yup.object({
  fromDate: yup.date().required(),
  toDate: yup
    .date()
    .nullable()
    .notRequired()
    .when('fromDate', (fromDate: Date, schema: any) =>
      fromDate
        ? schema.min(fromDate, 'End date cannot be before start date')
        : schema,
    ),
  fromTime: yup.string(),
  toTime: yup.string(),
  amount: yup.number(),
  category: yup.string().required(),
  project: yup.string().nullable(),
  description: yup.string().required(),
});

const tabKeys = {
  DETAILS: 'details',
  ITEMS: 'items',
};

const DEFAULT_EXPENSE_ITEM_RECORD_TYPE_LABEL = 'Other';
const MAX_FILE_SIZE = 1.5 * 1024 * 1024; // 1.5MB

export const ExpenseSaveForm: React.FC<Props> = ({ onSave }) => {
  const { addSuccess, addError } = useToasts();
  const { t } = useTranslation();
  const errorHandler = useMutationErrorHandler();
  const { data, loading } = useExpenseFormDataQuery();
  const [createExpense] = useCreateExpenseMutation();
  const mainFormRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [expenseItems, setExpenseItems] = useState<ExpenseItemState[]>([]);
  const expenseItemFormsRef = useRef<{ [key: string]: ExpenseItemFormRef }>({});
  const [activeTab, setActiveTab] = useState<string>(tabKeys.DETAILS);
  const [customMerchants, setCustomMerchants] = useState<
    CreatableDropdownOption[]
  >([]);
  const [expenseFiles, setExpenseFiles] = useState<File[]>([]);

  const projects = useMemo(
    () => data?.projects.map(mapProject) || [],
    [data?.projects],
  );
  const expenseCategories = useMemo(
    () => data?.expenseCategories.map(mapExpenseCategory) || [],
    [data?.expenseCategories],
  );
  const recordTypes = useMemo(
    () =>
      data?.recordTypes?.map((type) => ({
        value: type.Id,
        label: type.Name,
      })) || [],
    [data?.recordTypes],
  );

  const merchants = useMemo(() => {
    const apiMerchants = data?.merchants.map(mapMerchant) || [];
    return [...apiMerchants, ...customMerchants];
  }, [data?.merchants, customMerchants]);

  const submitAllExpenseItemForms = useCallback(async () => {
    try {
      await Promise.all(
        expenseItems.map(async (item) => {
          const formRef = expenseItemFormsRef.current[item.localId];
          if (formRef) {
            await formRef.submitForm();
          }
        }),
      );
    } catch (error) {
      errorHandler(error);
    }
  }, [expenseItems, errorHandler]);

  const hasInvalidExpenseItems = useCallback(() => {
    return expenseItems.some((item) => {
      const formRef = expenseItemFormsRef.current[item.localId];
      return formRef?.hasErrors?.() === true;
    });
  }, [expenseItems]);

  const handleSubmitAll = async () => {
    try {
      setIsSubmitting(true);

      if (expenseItems.length === 0) {
        setActiveTab(tabKeys.ITEMS);
        setIsSubmitting(false);
        return;
      }

      await submitAllExpenseItemForms();
      setTimeout(() => {
        if (hasInvalidExpenseItems()) {
          setActiveTab(tabKeys.ITEMS);
          setIsSubmitting(false);
          return;
        }

        const form = mainFormRef.current?.querySelector('form');
        if (form) {
          form.requestSubmit();
        } else {
          setIsSubmitting(false);
        }
      }, 100);
    } catch (error) {
      errorHandler(error);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (
    formData: FormData,
    extraFields: ExtraFieldInput[],
  ) => {
    const fromDate = moment(formData.fromDate).format('YYYY-MM-DD');
    const toDate = formData.toDate
      ? moment(formData.toDate).format('YYYY-MM-DD')
      : null;

    try {
      const expenseResult = await createExpense({
        variables: {
          input: {
            amount: formData.amount,
            category: formData.category,
            description: formData.description,
            expenseDate: fromDate,
            dateFrom: fromDate,
            dateTo: toDate,
            timeFrom: formData.fromTime,
            timeTo: formData.toTime,
            project: formData.project,
            extraFields,
            expenseItems: expenseItems.map((item) => ({
              recordTypeId: item.recordTypeId,
              manualAmount: item.manualAmount ?? null,
              merchantName: item.merchantName,
              merchantId: item.merchantId,
              files:
                item.files?.map((f) => ({
                  fileContentBase64: f.fileBase64,
                  fileName: f.fileName,
                })) || [],
              extraFields: item.extraFields || [],
            })),
            files: expenseFiles.map((f) => ({
              fileContentBase64: f.fileBase64,
              fileName: f.fileName,
            })),
          },
        },
      });

      if (expenseResult.data?.expenses.createExpense.error) {
        throw new Error(expenseResult.data.expenses.createExpense.error);
      }
      addSuccess(t('expenses.form.createSuccessText'));
      onSave();
      setIsSubmitting(false);
    } catch (error) {
      errorHandler(error);
      setIsSubmitting(false);
    }
  };

  const handleExpenseItemChange = useCallback(
    (id: string, field: keyof ExpenseItemState, value: any) => {
      setExpenseItems((items) =>
        items.map((item) =>
          item.localId === id ? { ...item, [field]: value } : item,
        ),
      );
    },
    [],
  );

  const handleAddMerchant = useCallback((value: string) => {
    setCustomMerchants((prev) => [...prev, { label: value, value }]);
  }, []);

  const onExpenseFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.validity.valid || !event.target.files) {
        return;
      }
      const eventsTarget = event.target;
      try {
        const files = await collectFiles(event.target.files);
        const filesTotalSize = files.reduce(
          (totalSize, file) => totalSize + file.size,
          0,
        );

        if (filesTotalSize > MAX_FILE_SIZE) {
          addError(t('expenses.form.sizeLimitError'));
          eventsTarget.value = '';
          return;
        }
        setExpenseFiles(files);
      } catch (error) {
        addError(t('expenses.form.fileUploadError'));
        if (eventsTarget) {
          eventsTarget.value = '';
        }
      }
    },
    [addError, t],
  );

  const handleAddExpenseItem = useCallback(async () => {
    try {
      await submitAllExpenseItemForms();
      const defaultRecordType =
        data?.recordTypes?.find(
          (rt) => rt.Name === DEFAULT_EXPENSE_ITEM_RECORD_TYPE_LABEL,
        ) ?? data?.recordTypes?.[0];

      setExpenseItems((prev) => [
        ...prev,
        {
          localId: Date.now().toString(),
          recordTypeId: defaultRecordType?.Id || '',
          merchantName: '',
          merchantId: '',
        },
      ]);
    } catch (error) {
      errorHandler(error);
    }
  }, [data?.recordTypes, submitAllExpenseItemForms, errorHandler]);

  const handleRemoveExpenseItem = useCallback(
    async (id: string) => {
      try {
        await submitAllExpenseItemForms();
        setExpenseItems((prev) => prev.filter((item) => item.localId !== id));
      } catch (error) {
        errorHandler(error);
      }
    },
    [submitAllExpenseItemForms, errorHandler],
  );

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1 overflow-hidden d-flex flex-column">
        <Tab.Container
          activeKey={activeTab}
          onSelect={(tabKey) => setActiveTab(tabKey || tabKeys.DETAILS)}>
          <Nav variant="tabs" className="mb-4 px-2">
            <Nav.Item>
              <Nav.Link eventKey="details">
                {t('expenses.form.tabs.details')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="items">
                {t('expenses.form.tabs.items')}
                {expenseItems.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-2">
                    {expenseItems.length}
                  </span>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content className="overflow-auto flex-grow-1 pe-3">
            <Tab.Pane eventKey="details">
              <Alert
                variant="light"
                className={classNames(
                  'd-flex',
                  'align-items-center',
                  'gap-3',
                  'mb-4',
                )}>
                <FlairIcon icon="information-circle-outline" size="2x" />
                <div> {t('expenses.form.tabs.detailsAlertInfo')}</div>
              </Alert>
              <div
                ref={mainFormRef}
                style={{ width: '100%', maxWidth: '100%' }}>
                <ExtraFieldsForm
                  objectApiName={ExtraFieldsSupportedObject.Expense}
                  formId={ExtraFieldsFormId.ExpensesRequestExpense}
                  onSubmit={handleSubmit}
                  validationSchema={formSchema}>
                  <div style={{ width: '100%', maxWidth: '100%' }}>
                    <Row className="g-2 mx-0">
                      <Col lg={6}>
                        <Datepicker
                          type="date"
                          required
                          name="fromDate"
                          label={t('expenses.form.fields.dateFromLabel')}
                        />
                      </Col>
                      <Col lg={6}>
                        <Datepicker
                          type="date"
                          name="toDate"
                          label={t('expenses.form.fields.dateToLabel')}
                        />
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <Input
                            type="time"
                            name="fromTime"
                            placeholder="HH:mm"
                            label={t('expenses.form.fields.timeFromLabel')}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <Input
                            type="time"
                            name="toTime"
                            placeholder="HH:mm"
                            label={t('expenses.form.fields.timeToLabel')}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <InputLabel
                            label={t('expenses.form.fields.categoryLabel')}
                            required={true}
                          />
                          <SelectDropdownControlled
                            isLoading={loading}
                            name="category"
                            placeholder={t(
                              'expenses.form.fields.categoryPlaceholder',
                            )}
                            options={expenseCategories}
                            heightMode="large"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <Form.Label>
                            {t('expenses.form.fields.projectLabel')}
                          </Form.Label>
                          <SelectDropdownControlled
                            isLoading={loading}
                            name="project"
                            placeholder={t(
                              'expenses.form.fields.projectPlaceholder',
                            )}
                            options={projects}
                            heightMode="large"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={12}>
                        <Input
                          name="description"
                          type="textarea"
                          label={t('expenses.form.fields.descriptionLabel')}
                          required
                        />
                      </Col>
                      <Col lg={12}>
                        <FormGroup>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Label className="mb-0">
                              {t('expenses.form.fields.fileLabelWithSize', {
                                size: fileSizeFormatter(MAX_FILE_SIZE),
                              })}
                            </Form.Label>
                            <OverlayTrigger
                              overlay={
                                <Tooltip id="expense-files-tooltip">
                                  {t('expenses.form.fields.fileTooltip')}
                                </Tooltip>
                              }
                              placement="top">
                              <span>
                                <FlairIcon
                                  icon="information-circle-outline"
                                  size="sm"
                                  style={{ cursor: 'help' }}
                                />
                              </span>
                            </OverlayTrigger>
                          </div>
                          <Form.Control
                            type="file"
                            multiple
                            onChange={onExpenseFileChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mx-0">
                      <Col lg={12}>
                        <ExtraFieldsArea
                          objectApiName={ExtraFieldsSupportedObject.Expense}
                          formId={ExtraFieldsFormId.ExpensesRequestExpense}
                        />
                      </Col>
                    </Row>
                  </div>
                </ExtraFieldsForm>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="items">
              {expenseItems.length === 0 && (
                <Alert
                  variant="warning"
                  className={classNames(
                    'd-flex',
                    'align-items-center',
                    'gap-3',
                    'mb-4',
                  )}>
                  <FlairIcon icon="information-circle-outline" />
                  <div>{t('expenses.form.tabs.itemsRequiredInfo')}</div>
                </Alert>
              )}

              <div className="m-1">
                {expenseItems.map((item, index) => (
                  <ExpenseItemForm
                    key={item.localId}
                    index={index}
                    recordTypes={recordTypes}
                    merchants={merchants}
                    maxFileSize={MAX_FILE_SIZE}
                    onAddMerchant={handleAddMerchant}
                    onRemove={() => handleRemoveExpenseItem(item.localId)}
                    onChange={(field, value) =>
                      handleExpenseItemChange(item.localId, field, value)
                    }
                    value={item}
                    ref={(ref) => {
                      if (ref) {
                        expenseItemFormsRef.current[item.localId] = ref;
                      }
                    }}
                  />
                ))}
              </div>

              <div className="text-end mb-3 mt-4">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddExpenseItem}>
                  {t('expenses.form.fields.addExpenseItem')}
                </Button>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      <div className="bg-white p-2 border-top pt-4 mt-2">
        <FlairButton
          variant="primary"
          size="lg"
          onClick={handleSubmitAll}
          className="w-100"
          isProcessing={isSubmitting}
          label={t('expenses.form.submitLabel')}
        />
      </div>
    </div>
  );
};
